import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type { Database } from '@jac/shared/generated/db-types'
import { usePrincipals, useSession } from '@/hooks/useSession'
import { PrincipalPicker } from '@/components/PrincipalPicker'
import { pt } from '@/i18n/pt-PT'

type Role = Database['public']['Enums']['user_role']

type RequireRoleProps = {
  roles: Role[]
  // Restrict to the PLATFORM OPERATOR (platform_admins), not to any firm. For cross-firm views
  // like admin Analytics that must never surface another firm's data. The DB
  // RPCs enforce the same gate — this is the UX mirror.
  platformOnly?: boolean
  children: ReactNode
}

// Client-side guard. The real access control is RLS on the DB — this is a UX
// check so unauthorised users see a clean redirect instead of blank query errors.
export function RequireRole({ roles, platformOnly, children }: RequireRoleProps) {
  const session = useSession()
  const { isPlatformAdmin } = usePrincipals()

  if (session.status === 'loading') {
    return <FullscreenMessage>{pt.auth.loading}</FullscreenMessage>
  }
  if (
    session.status === 'unauthenticated' ||
    session.status === 'needs_mfa_enroll' ||
    session.status === 'needs_mfa_challenge'
  ) {
    // Login is the auth hub — it renders the MFA enroll/challenge UI itself.
    return <Navigate to={{ pathname: '/login', search: window.location.search }} replace />
  }
  if (session.status === 'needs_principal_choice') {
    return <PrincipalPicker principals={session.principals} />
  }
  if (session.status === 'authenticated_client') {
    // The ACTIVE principal is a client-portal one. If this person also holds a staff principal,
    // offer the switch instead of bouncing: RequireRole and RequireClient bounce at each other, so
    // an unconditional redirect here becomes an infinite loop the moment someone holds both.
    const staff = session.principals.filter((p) => p.kind === 'staff')
    if (staff.length > 0) return <PrincipalPicker principals={session.principals} />
    return <Navigate to="/portal/inicio" replace />
  }
  if (session.status === 'unprovisioned') {
    return <FullscreenMessage>{pt.auth.unprovisioned}</FullscreenMessage>
  }
  if (!roles.includes(session.consultant.role)) {
    return <FullscreenMessage>{pt.auth.forbidden}</FullscreenMessage>
  }
  // Platform-operator views. This used to compare the active firm against DEFAULT_FIRM_ID, i.e.
  // "admin of the JA firm" — which conflated the platform OPERATOR with a TENANT and stops being
  // true the moment a second firm exists. It is now a person-level capability (platform_admins),
  // and the DB guards agree via is_platform_admin().
  if (platformOnly && !isPlatformAdmin) {
    return <FullscreenMessage>{pt.auth.forbidden}</FullscreenMessage>
  }

  return <>{children}</>
}

function FullscreenMessage({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-8 text-gray-700">
      <p className="rounded-lg border border-DEFAULT bg-surface px-6 py-4 text-[13px] shadow-card">
        {children}
      </p>
    </main>
  )
}
